import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from 'reactstrap';
import { shipmentHost } from '../../urls/api-shipments/merchant';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import fedex_logo_purple from '../../assets/img/Fedex-logo-purple.png';

// Set the worker path to the PDF.js worker file
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const FedexLabelModal = ({ isOpen, toggle, shipmentId }) => {
  const [labelData, setLabelData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfRendered, setPdfRendered] = useState(false);

  useEffect(() => {
    if (isOpen && shipmentId) {
      fetchLabel();
    }
  }, [isOpen, shipmentId]);

  const fetchLabel = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${shipmentHost}/shipments/${shipmentId}/label`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + localStorage.getItem('access_token')
        }
      });

      if (response.data.status === 'successful') {
        setLabelData(response.data.payload.labelBase64);
      } else {
        throw new Error('Failed to fetch label data');
      }
    } catch (err) {
      setError('Failed to fetch FedEx label. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!labelData) return;

    const printWindow = window.open('', '_blank');
    const pdfData = `data:application/pdf;base64,${labelData}`;

    printWindow.document.write(`
      <html>
        <head>
          <title>FedEx Label</title>
          <style>
            body { margin: 0; padding: 0; }
            canvas { width: 100%; height: auto; }
            @media print {
              body { margin: 0; padding: 0; }
              canvas { width: 100%; height: auto; }
            }
          </style>
        </head>
        <body>
          <canvas id="pdfCanvas"></canvas>
          <script src="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.min.js"></script>
          <script>
            const pdfData = "${pdfData}";
            pdfjsLib.getDocument({ data: atob(pdfData.split(',')[1]) }).promise.then(pdf => {
              pdf.getPage(1).then(page => {
                const viewport = page.getViewport({ scale: 1.5 });
                const canvas = document.getElementById('pdfCanvas');
                const context = canvas.getContext('2d');
                canvas.height = viewport.height;
                canvas.width = viewport.width;
                
                page.render({
                  canvasContext: context,
                  viewport: viewport
                }).promise.then(() => {
                  window.print();
                  window.onafterprint = function() {
                    window.close();
                  };
                });
              });
            });
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleDownload = () => {
    if (!labelData) return;

    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${labelData}`;
    link.download = `fedex-label.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePdfLoadSuccess = () => {
    setPdfRendered(true);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center">
          <img
            src={fedex_logo_purple}
            alt="FedEx"
            style={{
              width: '50px',
              height: '50px',
              marginLeft: '10px',
              marginRight: '5px',
              objectFit: 'contain'
            }}
          />
          Shipping Label
        </div>
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Loading shipping label...</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
            <Button color="link" className="p-0 ms-2" onClick={fetchLabel}>
              Try Again
            </Button>
          </div>
        ) : labelData ? (
          <div style={{ height: '500px', overflow: 'auto' }}>
            <Document file={`data:application/pdf;base64,${labelData}`} onLoadSuccess={handlePdfLoadSuccess}>
              <Page pageNumber={1} />
            </Document>
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
        {labelData && (
          <>
            <Button color="primary" onClick={handlePrint} disabled={!pdfRendered} style={{ padding: '0.5rem 1.5rem' }}>
              <i className="fas fa-print me-5"></i>
              &nbsp;Print Label
            </Button>
            <Button color="info" onClick={handleDownload} style={{ padding: '0.5rem 1.5rem' }}>
              <i className="fas fa-download me-5"></i>
              &nbsp;Download Label
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default FedexLabelModal;
